import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, firestore } from "../config/firebaseConfig";
import firebase from "firebase/compat/app";
import { toast } from "react-toastify";
import { apiClient } from "../services/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        const { uid, displayName, email } = firebaseUser;

        // ── Inject Firebase ID token into the API client ────────────────────
        try {
          const idToken = await firebaseUser.getIdToken();
          apiClient.setToken(idToken);
        } catch (tokenErr) {
          console.warn("Could not get Firebase ID token:", tokenErr);
          apiClient.clearToken();
        }

        // Read role from Firestore users collection
        try {
          const doc = await firestore.collection("users").doc(uid).get();
          const data = doc.exists ? doc.data() : {};
          setUser({
            uid,
            username: displayName || email,
            email,
            role: data.role ?? "member",
            status: data.status ?? "ACTIVE",
            name: data.name ?? displayName ?? email,
          });
        } catch (firestoreErr) {
          console.error("Firestore user read failed:", firestoreErr);
          toast.error("Could not load your profile. Some features may be limited.");
          // Still set user so app doesn't break — just without role
          setUser({ uid, username: displayName || email, email, role: "member" });
        }
      } else {
        // User signed out — clear the API client token
        apiClient.clearToken();
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Login using Firebase Auth (with session persistence)
  const login = async (email, password) => {
    try {
      // 👇 This ensures user is logged out when browser closes
      // await auth.setPersistence(firebase.auth.Auth.Persistence.SESSION);
      await auth.signInWithEmailAndPassword(email, password);
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  // Register using Firebase Auth
  const register = async (email, password, displayName) => {
    try {
      await auth.setPersistence(firebase.auth.Auth.Persistence.SESSION);
      const userCredential = await auth.createUserWithEmailAndPassword(email, password);
      await userCredential.user.updateProfile({ displayName });
      return userCredential.user.uid; // ← return uid for Firestore doc creation
    } catch (error) {
      console.error("Registration failed:", error);
      throw error;
    }
  };

  // Logout everywhere
  const logout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("Logout failed: " + (error.message || "Unknown error"));
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === "SUPER_ADMIN",
    isAdmin: user?.role === "ADMIN" || user?.role === "SUPER_ADMIN",
    login,
    register,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
